import { FC, lazy, ReactNode, Suspense, useCallback } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ApiProvider } from '@trukt/react-hooks/ApiProvider';
import { UIProviders } from '@trukt/ui/UIContext';

import { msalInstance } from '@auth';
import { MsalInstanceProvider } from '@trukt/auth-web/MsalInstanceProvider';
import { truktApi, truktTmsApi, trimbleMapsSingleSearchApi } from '@api';

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(async () => {
      const { ReactQueryDevtools } = await import('@tanstack/react-query-devtools');

      return { default: ReactQueryDevtools };
    })
  : null;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: true,
      refetchIntervalInBackground: false,
    },
    mutations: { retry: 0 },
  },
});
const authenticatedApis = [truktApi, truktTmsApi];

export const AppProviders: FC<Record<string, never | ReactNode>> = ({ children }) => {
  const routerNavigate = useNavigate();
  const navigate = useCallback(
    (path: string, options?: { replace?: boolean }) =>
      routerNavigate({ to: path, replace: options?.replace }),
    [routerNavigate],
  );

  return (
    <MsalInstanceProvider instance={msalInstance} apis={authenticatedApis} navigate={navigate}>
      <ApiProvider truktApi={truktTmsApi} trimbleApi={trimbleMapsSingleSearchApi}>
        <UIProviders appVariant="client">
          <QueryClientProvider client={queryClient}>
            {children}
            {ReactQueryDevtools ? (
              <Suspense fallback={null}>
                <ReactQueryDevtools initialIsOpen={false} />
              </Suspense>
            ) : null}
          </QueryClientProvider>
        </UIProviders>
      </ApiProvider>
    </MsalInstanceProvider>
  );
};
